import React from 'react';
import { Heading, Subheading, Label } from 'components/src/typography';
import { PackageSuggestionFormFieldFragment, FormInputFieldsFragment } from 'types/generated';
import Stepper from './Stepper';
import ArrowLeft from 'components/src/svg/ArrowLeft';
import InputForms from './InputForms';
import { centeredMainGridClasses } from '@/styles/grid';
import { usePackageSuggestionContext } from './PackageSuggestionContext';
import { getOr } from '@/utils/options';
import typewriter from '@/analytics/segment';

type Props = {
  data: PackageSuggestionFormFieldFragment['inputFormsCollection'];
  onClick: (step: number) => void;
  step: number;
  numberOfQuestions: number;
};

const renderBackButton = (onClick: (step: number) => void, step: number) => {
  return (
    <button onClick={() => onClick(step - 1)} className="flex">
      <ArrowLeft />
      <Label classes="my-auto ml-4 inline !text-md text-red-100 lg:ml-8 lg:!text-sm">{'Til baka'}</Label>
    </button>
  );
};

const QuestionSection = ({ data, onClick, step, numberOfQuestions }: Props) => {
  const { state } = usePackageSuggestionContext();
  const pageStep = step - 1;
  const inputFormsForQuestion = data?.items[pageStep < 0 ? 1 : pageStep];

  const canContinue: boolean = React.useMemo(() => {
    return (inputFormsForQuestion?.inputFieldsCollection?.items || [])
      .map((x) => {
        const inputForms = state.inputForms;
        if (x?.id === 'heimilisfang' || x?.id === 'postnumer' || x?.inputType === 'checkbox') {
          return true;
        }
        if (x?.id) {
          return inputForms[x.id] === undefined || inputForms[x.id] === '' ? false : true;
        }
        return false;
      })
      .every(Boolean);
  }, [state, inputFormsForQuestion?.inputFieldsCollection?.items]);

  if (!data?.items) {
    return;
  }

  return (
    <div className={`${centeredMainGridClasses} self-center`}>
      <div className="col-span-full lg:hidden">{renderBackButton(onClick, step)}</div>
      <div className="col-span-full col-start-1 my-40 grid grid-flow-col justify-center self-center lg:col-span-1 lg:my-0 lg:grid-flow-row">
        {[...Array(numberOfQuestions)].map((_, index) => (
          <Stepper key={index} step={index + 1} currentStep={step} />
        ))}
      </div>
      <div className="col-span-full lg:col-span-5 lg:col-start-2 lg:self-center">
        <div className="mb-16 hidden lg:flex">{renderBackButton(onClick, step)}</div>
        <Subheading classes="mb-8 text-black-75" small bold>{`Skref ${step}`}</Subheading>

        <div key={inputFormsForQuestion?.id}>
          {inputFormsForQuestion?.title && (
            <Heading variant="small" classes="text-black-100">
              {inputFormsForQuestion.title}
            </Heading>
          )}
          {inputFormsForQuestion?.description && (
            <Subheading classes="mt-16 text-black-100">{inputFormsForQuestion.description}</Subheading>
          )}
          <div className="mt-32">
            <InputForms
              data={inputFormsForQuestion?.inputFieldsCollection?.items as Array<FormInputFieldsFragment>}
              buttonText={getOr(inputFormsForQuestion?.button?.label, 'Næsta')}
              canContinue={canContinue}
              onNextClick={() => {
                const nextStep = step + 1;
                const nextStepTitle =
                  nextStep <= numberOfQuestions
                    ? getOr(data.items?.[nextStep - 1]?.title, 'Enginn titill fyrir skref')
                    : 'Tillaga að pakka';
                typewriter.packageSuggestionStepViewed({
                  step: nextStep,
                  stepTitle: nextStepTitle,
                });
                onClick(nextStep);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
export default QuestionSection;
